import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl, FormHelperText, FormLabel, MenuItem, Select, Typography } from '@/core/ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { t } from '@/core/localization';
import { getBlockReasonLabel } from '@/shared/helpers';
import { getBlockReasonIds } from './getBlockReasonIds';
const timeSpentOnFocus = 10;
/** Currently used in **Warehouse** app only. */
const BlockReasonSelect = ({ ownerRef, focusRef, disabled, isTrackable, ...useControllerProps }) => {
    const { field, fieldState: { invalid, error }, } = useController(useControllerProps);
    const blockReasonIds = useMemo(() => getBlockReasonIds(isTrackable ?? false), [isTrackable]);
    const handleOwnerKeyDown = useCallback((event) => {
        // Numpad - digits from numpad part of keyboard
        const targetDigitKeyCodes = [...blockReasonIds.map((id) => `Digit${id}`), ...blockReasonIds.map((id) => `Numpad${id}`)];
        if (event.shiftKey && targetDigitKeyCodes.includes(event.code)) {
            const reasonId = Number(event.code.slice(-1));
            field.onChange(reasonId);
            focusRef?.current?.focus();
            setTimeout(() => focusRef?.current?.select?.(), timeSpentOnFocus);
            return;
        }
    }, [blockReasonIds, field, focusRef]);
    useEffect(() => {
        // Handling eslint error:
        // The ref value 'ownerRef.current' will likely have changed by the time this effect cleanup function runs. If this
        // ref points to a node rendered by React, copy 'ownerRef.current' to a variable inside the effect, and use that
        // variable in the cleanup function. eslint react-hooks/exhaustive-deps
        const ownerElement = ownerRef.current;
        ownerElement?.addEventListener('keydown', handleOwnerKeyDown);
        return () => {
            ownerElement?.removeEventListener('keydown', handleOwnerKeyDown);
        };
    }, [handleOwnerKeyDown, ownerRef]);
    return (_jsxs(FormControl, { error: invalid, disabled: disabled, children: [_jsx(FormLabel, { children: t('reason.label') }), _jsx(Select, { ...field, autoFocus: true, children: blockReasonIds.map((reason) => (_jsx(MenuItem, { value: reason, children: getBlockReasonLabel(reason, true) }, reason))) }), _jsx(FormHelperText, { component: "span", children: error?.message ?? (_jsx(Typography, { fontSize: "inherit", color: "textDisabled", children: `Shift + ${blockReasonIds.join('/')}` })) })] }));
};
export { BlockReasonSelect };
